import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette } from '../components/ui';
import { FormulaireClient } from '../components/FormulaireClient';
import { ajouteJours, centimes, dateFr, isoJour, montant, nomClient } from '../lib/format';
import type { Blocage, Client, Vehicule, Verdict } from '../api/types';

interface Dispo { disponibles: { vehicle: Vehicule; totalMinor: number; tarifJourMinor: number; saison: string }[] }

/**
 * L'assistant en trois etapes. L'etape 2 est celle qui compte :
 * l'article 01 est calcule en direct, il bloque, et seul un gerant peut deroger
 * — en laissant son nom au journal.
 */
interface SuppDepart {
  _id: string; code: string; libelle: string; unite: string; prixMinor: number;
  moment: string; actif: boolean;
}

export default function NouvelleReservation() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { peut } = useAuth();

  const [etape, setEtape] = useState(1);
  const [debut, setDebut] = useState(params.get('debut') ?? isoJour(new Date()));
  const [fin, setFin] = useState(params.get('fin') ?? isoJour(ajouteJours(new Date(), 7)));
  const [dispo, setDispo] = useState<Dispo | null>(null);
  const [vehiculeId, setVehiculeId] = useState(params.get('vehicle') ?? '');

  const [recherche, setRecherche] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [client, setClient] = useState<Client | null>(null);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [derogations, setDerogations] = useState<Record<string, string>>({});
  const [demandeDerog, setDemandeDerog] = useState<Blocage | null>(null);
  const [nouveauClient, setNouveauClient] = useState(false);
  const [motifSaisi, setMotifSaisi] = useState('');

  const [tarifNegocie, setTarifNegocie] = useState<number | ''>('');
  const [kmIllimite, setKmIllimite] = useState(false);
  const [erreur, setErreur] = useState<ApiError | null>(null);
  const [verdictKo, setVerdictKo] = useState(false);
  // « Un seul catalogue de supplements — le siege bebe et le retard dans la meme
  // liste » : la moitie « retour » etait cablee, la moitie « depart » n'existait
  // pas. Le serveur acceptait deja `supplements`, aucun ecran ne les proposait.
  const [catalogue, setCatalogue] = useState<SuppDepart[]>([]);
  const [gerantPrevenu, setGerantPrevenu] = useState(false);
  const [envoiPrevenir, setEnvoiPrevenir] = useState(false);
  const [quantites, setQuantites] = useState<Record<string, number>>({});
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (fin > debut) api.get<Dispo>(`/disponibilites${qs({ debut, fin })}`).then(setDispo);
  }, [debut, fin]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get<Client[]>(`/clients${qs({ q: recherche })}`).then(setClients).catch(() => {});
    }, 220);
    return () => clearTimeout(t);
  }, [recherche]);

  useEffect(() => {
    if (!client) { setVerdict(null); setVerdictKo(false); return; }
    setVerdictKo(false);
    api.post<Verdict>('/clients/verifier', {
      dateNaissance: client.dateNaissance, permisDelivreLe: client.permisDelivreLe,
      listeNoire: client.listeNoire, motifListeNoire: client.motifListeNoire,
    }).then((v) => { setVerdict(v); setVerdictKo(false); })
      // Si la verification tombe, on NE PASSE PAS. Une liste de blocages vide
      // parce que l'appel a echoue est indiscernable d'un client conforme :
      // c'est precisement comme ca que l'article 01 se neutralise en silence.
      .catch(() => { setVerdict(null); setVerdictKo(true); });
  }, [client]);

  useEffect(() => {
    api.get<SuppDepart[]>('/supplements')
      .then((l) => setCatalogue(l.filter((x) => x.actif && x.moment === 'depart')))
      .catch(() => setCatalogue([]));
  }, []);

  const choisi = useMemo(() => dispo?.disponibles.find((x) => x.vehicle._id === vehiculeId), [dispo, vehiculeId]);
  const jours = Math.max(1, Math.ceil((+new Date(fin) - +new Date(debut)) / 86400000));
  const tarifRef = choisi?.tarifJourMinor ?? 0;
  const tarifApplique = tarifNegocie === '' ? tarifRef : centimes(Number(tarifNegocie));
  const ecartPct = tarifRef ? Math.round(((tarifApplique - tarifRef) / tarifRef) * 1000) / 10 : 0;
  const lignesSupp = catalogue
    .filter((x) => (quantites[x.code] ?? 0) > 0)
    .map((x) => ({ ...x, quantite: quantites[x.code]!, totalMinor: x.prixMinor * quantites[x.code]! }));
  const totalSupp = lignesSupp.reduce((a, l) => a + l.totalMinor, 0);
  const totalLocation = tarifApplique * jours;
  const totalGeneral = totalLocation + totalSupp;

  const bloquantsRestants = (verdict?.blocages ?? []).filter((b) => !derogations[b.regle]);
  // `verdict` non nul est exige : pas de verification, pas de passage.
  const peutContinuer = Boolean(client) && Boolean(verdict) && bloquantsRestants.length === 0;

  async function creer(statut: 'option' | 'confirmee') {
    if (!choisi || !client) return;
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.post<{ reservation: { _id: string } }>('/reservations', {
        vehicle: choisi.vehicle._id, customer: client._id,
        dateDebut: new Date(`${debut}T09:00:00`).toISOString(),
        dateFin: new Date(`${fin}T09:00:00`).toISOString(),
        tarifNegocieJourMinor: tarifApplique, kmIllimite, statut,
        supplements: lignesSupp.map((l) => ({ code: l.code, quantite: l.quantite })),
        derogations: Object.entries(derogations).map(([regle, motif]) => ({ regle, motif })),
      });
      nav(`/reservations/${r.reservation._id}`);
    } catch (e) {
      if (e instanceof ApiError) setErreur(e);
    } finally { setEnvoi(false); }
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Nouvelle location</h1>
          <p>Étape {etape} sur 3 — {['dates et véhicule', 'client', 'prix et validation'][etape - 1]}</p>
        </div>
      </div>

      {/* ---------- Etape 1 ---------- */}
      {etape === 1 && (
        <Carte titre="Dates et véhicule">
          <div className="grille g2" style={{ marginBottom: 14 }}>
            <Champ label="Départ"><input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} /></Champ>
            <Champ label="Retour"><input type="date" min={debut} value={fin} onChange={(e) => setFin(e.target.value)} /></Champ>
          </div>
          {!dispo ? <Squelette /> : dispo.disponibles.length === 0 ? (
            <Bandeau type="warn" titre="Rien de libre sur cette période">Changez les dates, ou consultez l'écran Disponibilités pour voir quand les voitures se liberent.</Bandeau>
          ) : (
            <div className="enveloppe-tbl">
              <table className="tbl">
                <thead><tr><th /><th>Véhicule</th><th>Immatriculation</th><th className="droite">Tarif / jour</th><th className="droite">Total {jours} j</th></tr></thead>
                <tbody>
                  {dispo.disponibles.map((x) => (
                    <tr key={x.vehicle._id} onClick={() => setVehiculeId(x.vehicle._id)} style={{ cursor: 'pointer' }}>
                      <td><input type="radio" checked={vehiculeId === x.vehicle._id} onChange={() => setVehiculeId(x.vehicle._id)} /></td>
                      <td><b>{x.vehicle.marque} {x.vehicle.modele}</b> <span className="mini sourdine">{x.vehicle.categorie}</span></td>
                      <td className="num">{x.vehicle.immatriculation}</td>
                      <td className="droite montant">{montant(x.tarifJourMinor)}</td>
                      <td className="droite montant">{montant(x.totalMinor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            {/* `vehiculeId` peut venir de l'URL et ne correspondre a aucune ligne
                (dates changees depuis). Se fier a l'identifiant menait a une
                etape 3 vide, sans recapitulatif ni bouton Retour. */}
            <button className="btn btn-p btn-l" disabled={!choisi} onClick={() => setEtape(2)}>Suivant</button>
          </div>
        </Carte>
      )}

      {/* ---------- Etape 2 : article 01 ---------- */}
      {etape === 2 && (
        <Carte titre="Client">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
            <Champ label="Rechercher par nom ou téléphone">
              <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Bouzidi, 0661…" autoFocus />
            </Champ>
            <button className="btn" onClick={() => setNouveauClient(true)}>Nouveau client</button>
          </div>

          <div style={{ marginTop: 12, display: 'grid', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
            {clients.map((c) => (
              <button key={c._id} className="btn" style={{ justifyContent: 'space-between', height: 'auto', padding: '10px 12px' }}
                      onClick={() => setClient(c)} aria-pressed={client?._id === c._id}>
                <span style={{ textAlign: 'left' }}>
                  <b>{nomClient(c)}</b>
                  <div className="mini sourdine">{c.telephone} · {c.pieceType} {c.pieceNumero}</div>
                </span>
                {c.listeNoire && <Badge statut="retard" libelle="Liste noire" />}
              </button>
            ))}
          </div>

          {client && verdictKo && (
            <div style={{ marginTop: 16 }}>
              <Bandeau type="ko" titre="Vérification impossible">
                Les règles de l'article 01 — âge minimum et ancienneté du permis — n'ont pas pu être
                vérifiées auprès du serveur. Tant que la vérification n'aboutit pas, la location ne
                peut pas être créée : sans elle, un client non conforme passerait sans que personne
                ne le voie.
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-s" onClick={() => setClient({ ...client })}>Réessayer</button>
                </div>
              </Bandeau>
            </div>
          )}

          {client && verdict && (
            <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
              <div className="grille g2">
                <Champ label="Date de naissance" ok={verdict.age !== undefined && verdict.age >= 28}
                       erreur={verdict.blocages.some((b) => b.regle === 'age_minimum') ? `${verdict.age} ans — minimum 28 ans exige (article 01)` : undefined}
                       aide={verdict.age !== undefined ? `${verdict.age} ans` : undefined}>
                  <input readOnly value={dateFr(client.dateNaissance)} />
                </Champ>
                <Champ label="Permis délivré le" ok={(verdict.ancienneteParmis ?? 0) >= 5}
                       erreur={verdict.blocages.some((b) => b.regle === 'anciennete_permis') ? `Permis de ${verdict.ancienneteParmis} an(s) — minimum 5 ans exige (article 01)` : undefined}
                       aide={verdict.ancienneteParmis !== undefined ? `${verdict.ancienneteParmis} ans d'anciennete` : undefined}>
                  <input readOnly value={dateFr(client.permisDelivreLe)} />
                </Champ>
              </div>

              {verdict.blocages.map((b) => {
                const levee = derogations[b.regle];
                return (
                  <div key={b.regle}>
                    {levee ? (
                      <Bandeau type="warn" titre={`Derogation accordee — ${b.message}`}>Motif : {levee}</Bandeau>
                    ) : (
                      <div className="bandeau ko">
                        <div style={{ flex: 1 }}>
                          <b>{b.message}</b>
                          {b.detail}
                          <div style={{ marginTop: 10 }}>
                            {/* Une information absente de la fiche ne se « deroge » pas :
                                elle se saisit. Proposer une derogation ici revenait a
                                offrir de signer un controle qu'on n'a pas pu faire. */}
                            {!b.derogeable ? (
                              <>
                                <div className="mini" style={{ marginBottom: 8 }}>
                                  Cette information manque à la fiche du client : elle ne peut pas
                                  faire l'objet d'une dérogation, même par un gérant.
                                </div>
                                <button className="btn btn-s" onClick={() => setClient(null)}>
                                  Compléter la fiche client
                                </button>
                              </>
                            ) : peut('accorder.derogation') ? (
                              <button className="btn btn-s" onClick={() => { setDemandeDerog(b); setMotifSaisi(''); }}>Autoriser malgré tout</button>
                            ) : (
                              <button className="btn btn-s" onClick={() => { setDemandeDerog(b); setMotifSaisi(''); }}>Prévenir un gérant</button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {verdict.conforme && <Bandeau type="ok" titre="Article 01 respecte">Age et ancienneté de permis conformes.</Bandeau>}
            </div>
          )}

          <div className="actions" style={{ marginTop: 16, justifyContent: 'space-between' }}>
            <button className="btn" onClick={() => setEtape(1)}>Retour</button>
            <button className="btn btn-p btn-l" disabled={!peutContinuer} onClick={() => setEtape(3)}>Suivant</button>
          </div>
        </Carte>
      )}

      {/* ---------- Etape 3 ---------- */}
      {etape === 3 && !(choisi && client) && (
        <Carte titre="Cette voiture n'est plus disponible">
          <p className="mini sourdine">
            La voiture retenue ne figure plus dans les disponibilités de ces dates — elle a
            probablement été prise entre-temps, ou les dates ont changé. Revenez à l'étape 1
            pour en choisir une autre : rien n'a été enregistré.
          </p>
          <div className="actions" style={{ marginTop: 14 }}>
            <button className="btn btn-p btn-l" onClick={() => setEtape(1)}>Revenir au choix de la voiture</button>
          </div>
        </Carte>
      )}

      {etape === 3 && choisi && client && (
        <div className="grille g2" style={{ alignItems: 'start' }}>
          <Carte titre="Prix negocie">
            <p className="mini sourdine" style={{ marginBottom: 12 }}>
              Le tarif affiche est un point de depart. L'écart n'est jamais bloqué — il est mesuré.
            </p>
            <div className="grille g2">
              <Champ label="Tarif de référence / jour"><input readOnly value={montant(tarifRef)} /></Champ>
              <Champ label="Prix negocie / jour (DZD)"
                     aide={ecartPct !== 0 ? `${ecartPct > 0 ? '+' : ''}${ecartPct} % par rapport au tarif de reference` : 'au tarif de référence'}>
                <input type="number" min={0} step={100} value={tarifNegocie}
                       placeholder={String(tarifRef / 100)} onChange={(e) => setTarifNegocie(e.target.value === '' ? '' : Number(e.target.value))} />
              </Champ>
            </div>
            <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 12 }}>
              <input type="checkbox" checked={kmIllimite} onChange={(e) => setKmIllimite(e.target.checked)} />
              Kilométrage illimité <span className="mini sourdine">(sinon 250 km/jour, article 05)</span>
            </label>
          </Carte>

          <Carte titre="Suppléments du départ">
            <p className="mini sourdine" style={{ marginBottom: 12 }}>
              Un seul catalogue : le siège bébé et le chauffeur additionnel vivent dans la même
              liste que le retard et le carburant. Ceux-ci s'ajoutent au départ.
            </p>
            {catalogue.length === 0 ? (
              <p className="mini sourdine">
                Aucun supplément de départ au catalogue. Un gérant peut en ajouter dans
                <b> Paramètres › Suppléments</b>.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {catalogue.map((x) => (
                  <div key={x.code} style={{ display: 'grid', gridTemplateColumns: '1fr 96px', gap: 10, alignItems: 'center' }}>
                    <div>
                      <div>{x.libelle} <span className="mini sourdine">/ {x.unite}</span></div>
                      {x.prixMinor === 0
                        ? <div className="mini" style={{ color: 'var(--danger)' }}>
                            Prix non saisi — il serait facturé 0 DZD. À renseigner dans Paramètres.
                          </div>
                        : <div className="mini sourdine">{montant(x.prixMinor)}</div>}
                    </div>
                    <input type="number" min={0} inputMode="numeric"
                           value={quantites[x.code] ?? ''}
                           placeholder="0"
                           onChange={(e) => setQuantites({ ...quantites, [x.code]: Math.max(0, Number(e.target.value) || 0) })} />
                  </div>
                ))}
              </div>
            )}
          </Carte>

          <Carte titre="Récapitulatif">
            <table className="tbl compact">
              <tbody>
                <tr><td>Véhicule</td><td className="droite"><b>{choisi.vehicle.marque} {choisi.vehicle.modele}</b><div className="mini sourdine">{choisi.vehicle.immatriculation}</div></td></tr>
                <tr><td>Client</td><td className="droite">{nomClient(client)}</td></tr>
                <tr><td>Période</td><td className="droite">{dateFr(debut)} → {dateFr(fin)}</td></tr>
                <tr><td>{jours} jour(s) x {montant(tarifApplique)}</td><td className="droite montant">{montant(tarifApplique * jours)}</td></tr>
                <tr><td>Kilométrage</td><td className="droite">{kmIllimite ? 'illimité' : `250 km / jour`}</td></tr>
                {lignesSupp.map((l) => (
                  <tr key={l.code}>
                    <td>{l.libelle} <span className="mini sourdine">{l.quantite} × {montant(l.prixMinor)}</span></td>
                    <td className="droite montant">{montant(l.totalMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--bord)' }}>
              <b>Total</b><b className="montant" style={{ fontSize: 22 }}>{montant(totalGeneral)}</b>
            </div>

            {Object.keys(derogations).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Bandeau type="warn" titre="Dérogation a l'article 01">Elle sera enregistrée au journal à votre nom.</Bandeau>
              </div>
            )}
            {erreur && (
              <div style={{ marginTop: 12 }}>
                <Bandeau type="ko" titre={erreur.code === 'CHEVAUCHEMENT' ? 'Chevauchement' : 'Impossible de creer'}>{erreur.erreur}</Bandeau>
              </div>
            )}

            <div className="actions" style={{ marginTop: 14, justifyContent: 'space-between' }}>
              <button className="btn" onClick={() => setEtape(2)}>Retour</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" disabled={envoi} onClick={() => void creer('option')}>Poser une option</button>
                <button className="btn btn-p btn-l" disabled={envoi} onClick={() => void creer('confirmee')}>Confirmer</button>
              </div>
            </div>
          </Carte>
        </div>
      )}

      {nouveauClient && (
        <FormulaireClient onFermer={() => setNouveauClient(false)}
                          onCree={(c, v) => { setNouveauClient(false); setClient(c); setVerdict(v); setRecherche(c.nom); }} />
      )}

      {demandeDerog && (
        <Modale titre={peut('accorder.derogation') ? 'Autoriser malgré tout' : 'Demander au responsable'} onFermer={() => setDemandeDerog(null)}>
          <Bandeau type="warn" titre={demandeDerog.message}>{demandeDerog.detail}</Bandeau>
          <div style={{ marginTop: 14 }}>
            <Champ label="Motif (obligatoire)" aide="Il sera conserve au journal, avec votre nom.">
              <textarea value={motifSaisi} onChange={(e) => setMotifSaisi(e.target.value)} placeholder="Client connu, accompagne par un conducteur experimente…" />
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setDemandeDerog(null)}>Annuler</button>
            {peut('accorder.derogation') ? (
              <button className="btn btn-p" disabled={motifSaisi.trim().length < 3}
                      onClick={() => { setDerogations({ ...derogations, [demandeDerog.regle]: motifSaisi.trim() }); setDemandeDerog(null); }}>
                Accorder la dérogation
              </button>
            ) : (
              /*
               * L'ancien texte promettait qu'un gerant « autorise depuis son
               * telephone » — cet ecran n'existe pas : la derogation ne peut
               * etre accordee que sur CE poste. On ne promet donc plus ce qu'on
               * ne sait pas faire, et on laisse une trace reelle dans la file
               * « À valider » du gerant, pour qu'il sache qu'un client attend.
               */
              <button className="btn btn-p" disabled={motifSaisi.trim().length < 3 || envoiPrevenir || gerantPrevenu}
                      onClick={async () => {
                        if (!client) return;
                        setEnvoiPrevenir(true);
                        try {
                          await api.post('/corrections', {
                            entite: 'Client', entiteId: client._id,
                            libelleEntite: nomClient(client),
                            champ: `dérogation article 01 — ${demandeDerog.message}`,
                            valeurActuelle: demandeDerog.detail,
                            valeurDemandee: 'Autoriser la location',
                            motif: motifSaisi.trim(), natureFinanciere: false,
                          });
                          setGerantPrevenu(true);
                        } catch { /* le bandeau ci-dessous reste affiche */ }
                        finally { setEnvoiPrevenir(false); }
                      }}>
                {gerantPrevenu ? 'Gérant prévenu' : envoiPrevenir ? 'Envoi…' : 'Prévenir un gérant'}
              </button>
            )}
          </div>
          {!peut('accorder.derogation') && (
            <div style={{ marginTop: 12 }}>
              {gerantPrevenu ? (
                <Bandeau type="ok" titre="Votre demande est partie">
                  Elle apparaît dans <b>« À valider »</b> chez Reda et Hichem, avec le nom du client
                  et votre motif. <b>La dérogation se donne sur cet écran</b> : quand l'un d'eux
                  arrive, il se connecte ici avec son compte et lève le blocage.
                </Bandeau>
              ) : (
                <p className="mini sourdine">
                  Vous ne pouvez pas lever ce blocage vous-même. Écrivez le motif et prévenez un
                  gérant : il devra venir lever le blocage sur ce poste, en se connectant avec son
                  compte. Son nom restera au journal.
                </p>
              )}
            </div>
          )}
        </Modale>
      )}
    </>
  );
}
